import { z } from 'zod';
import { simulationEnvironmentSchema, simulationEnvironmentSourceSchema, simulationMediaKindSchema, simulationSkySchema, simulationStatusSchema, simulationThreatApproachSchema, simulationThreatTrajectorySchema, simulationThreatTypeSchema, simulationTimeOfDaySchema, } from '../enums.js';
/**
 * Simulation de données d'entraînement synthétiques (ADR-0021).
 *
 * Le **scénario** est l'artefact de première classe : il décrit la scène à rendre. Le rendu, lui,
 * se fait hors plateforme (nœud GPU) et reste dormant tant que `SIMULATION_ENABLED` n'est pas actif.
 * Ce contrat est partagé API / front / worker : écrit à la main ou proposé par l'assistant, il est
 * validé ici une fois pour toutes.
 */
/**
 * Une **cible** à faire apparaître dans la scène — l'objet que le modèle devra détecter. Le `label`
 * provient du référentiel de l'organisation (FR-27) : les boîtes synthétiques naissent donc avec un
 * label métier, exploitables sans réétiquetage. La `density` borne le nombre d'instances par image
 * pour rester rendable.
 */
export const simulationTargetSchema = z.object({
    label: z.string().trim().min(1).max(120),
    density: z.number().int().min(1).max(200).default(1),
});
/**
 * Une **menace aérienne** — drone hostile à détecter (ADR-0023). Deux identités distinctes :
 *
 * - `type` : le type **physique** (FPV, voilure fixe…), **figé côté contrat**, qui pilote le modèle
 *   3D et la cinématique par défaut au rendu ;
 * - `label` : le **nom de classe** appris par le modèle, repris du **référentiel de l'organisation**
 *   (FR-27), comme les cibles au sol. `null` ⇒ le nom du type sert de classe. Ainsi les boîtes
 *   synthétiques peuvent porter le vocabulaire d'annotation réel, fusionnables sans remappage.
 *
 * La cinématique décrit le mouvement : une **plage de vitesse**, un axe d'approche, une trajectoire,
 * un `count` (un essaim = une menace à N instances) et une `dispersionDeg` (étalement des caps).
 */
export const simulationThreatSchema = z
    .object({
    type: simulationThreatTypeSchema,
    label: z.string().trim().min(1).max(120).nullable().default(null),
    /** Taille caractéristique en pouces (hélice FPV/quad) ; `null` si non pertinent. */
    sizeInches: z.number().min(1).max(60).nullable().default(null),
    /** Nombre d'instances. > 1 = essaim. Borné pour rester rendable. */
    count: z.number().int().min(1).max(50).default(1),
    speedMinMs: z.number().min(1).max(120).default(10),
    speedMaxMs: z.number().min(1).max(120).default(20),
    approach: simulationThreatApproachSchema.default('FRONTAL'),
    trajectory: simulationThreatTrajectorySchema.default('PASS'),
    /** Étalement des caps (degrés) pour une dispersion d'essaim ; `null` hors dispersion. */
    dispersionDeg: z.number().min(0).max(180).nullable().default(null),
})
    .refine((t) => t.speedMaxMs >= t.speedMinMs, {
    message: 'La vitesse maximale doit être ≥ à la minimale.',
    path: ['speedMaxMs'],
});
/**
 * Sorties demandées pour un lot. On peut vouloir des **images étiquetées**, une **vidéo**, ou les
 * deux (ADR-0021) — mais au moins une : un lot sans sortie n'a pas de sens.
 */
export const simulationOutputsSchema = z
    .object({
    labeledImages: z.boolean().default(true),
    video: z.boolean().default(false),
})
    .refine((o) => o.labeledImages || o.video, {
    message: 'Au moins une sortie doit être demandée (images ou vidéo).',
});
/**
 * Un **média de référence** pour la reconstruction (ADR-0023, incrément C) : un pointeur cloisonné
 * vers une vidéo ou une image **déjà sur la plateforme**. On ne copie pas le fichier — l'octet ne
 * traverse pas l'API ; le nœud GPU le lira depuis le stockage objet. L'appartenance à l'organisation
 * est vérifiée à la création du lot.
 */
export const simulationReferenceMediaSchema = z.object({
    kind: simulationMediaKindSchema,
    id: z.string().min(1),
});
/**
 * Spécification complète d'un scénario : la scène et ce qu'on en attend. Les bornes numériques
 * cadrent le domaine réaliste d'un vol drone et gardent un lot rendable par le nœud GPU.
 */
export const simulationScenarioSchema = z
    .object({
    environment: simulationEnvironmentSchema,
    /**
     * Source du décor (ADR-0023) : `PRESET` (générique) ou `RECONSTRUCTION` (depuis les médias de la
     * zone). En reconstruction, `environment` reste un repli/métadonnée ; moment et ciel ré-éclairent.
     */
    environmentSource: simulationEnvironmentSourceSchema.default('PRESET'),
    /** Médias de référence pour la reconstruction. Requis (≥1) quand la source est RECONSTRUCTION. */
    referenceMedia: z.array(simulationReferenceMediaSchema).max(50).default([]),
    timeOfDay: simulationTimeOfDaySchema,
    sky: simulationSkySchema,
    // Cibles au sol (personne, véhicule…) et menaces aériennes (drones) sont **indépendantes** et
    // peuvent coexister (ADR-0023). L'une des deux au moins doit être renseignée (voir refine).
    targets: z.array(simulationTargetSchema).max(20).default([]),
    threats: z.array(simulationThreatSchema).max(20).default([]),
    /** Altitude de vol (m) — cadre l'échelle apparente des cibles. Domaine drone réaliste. */
    altitudeM: z.number().int().min(5).max(400).default(60),
    /** Tangage de nacelle, degrés sous l'horizon : 0 = visée horizontale, 90 = nadir (vue du dessus). */
    gimbalPitchDeg: z.number().int().min(0).max(90).default(45),
    outputs: simulationOutputsSchema,
    /** Nombre d'images à rendre. Borné : un lot doit rester raisonnable pour le nœud de rendu. */
    imageCount: z.number().int().min(1).max(2000).default(200),
    /** Durée du clip vidéo (s), exploitée seulement si `outputs.video`. */
    videoDurationSeconds: z.number().int().min(1).max(120).default(20),
    /**
     * Graine de reproductibilité : rejouer un lot à graine égale redonne la même scène. Sert
     * l'idempotence (PRD NFR-13) ; `null` = graine tirée au lancement.
     */
    seed: z.number().int().min(0).max(2_147_483_647).nullable().default(null),
})
    .refine((s) => s.targets.length + s.threats.length >= 1, {
    message: 'Renseignez au moins une cible au sol ou une menace aérienne.',
    path: ['targets'],
})
    .refine((s) => s.environmentSource !== 'RECONSTRUCTION' || s.referenceMedia.length >= 1, {
    message: 'La reconstruction exige au moins un média de référence.',
    path: ['referenceMedia'],
});
/**
 * Création d'un lot : un nom lisible + le scénario. Le lot naît `DRAFT` ; le **lancer** (rendu) est
 * une action distincte, refusée `503` tant que `SIMULATION_ENABLED` est dormant (ADR-0021 §3).
 */
export const createSimulationBatchSchema = z.object({
    name: z.string().trim().min(1).max(200),
    scenario: simulationScenarioSchema,
});
/**
 * DTO d'un lot renvoyé par l'API. `imageCount` est le nombre **effectivement** produit (0 tant que
 * le rendu n'a pas eu lieu) ; `videoId` pointe la vidéo synthétique quand elle existe. Tout lot est
 * une donnée **synthétique** : la provenance est portée par l'entité elle-même, jamais devinée.
 */
export const simulationBatchSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    name: z.string(),
    scenario: simulationScenarioSchema,
    status: simulationStatusSchema,
    imageCount: z.number().int().nonnegative(),
    videoId: z.string().nullable(),
    error: z.string().nullable(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    // Revue humaine (ADR-0023) : qui a validé/rejeté et quand ; motif en cas de rejet. Nuls tant que
    // le lot n'a pas été revu.
    reviewedByName: z.string().nullable(),
    reviewedAt: z.coerce.date().nullable(),
    rejectionReason: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Rejet d'un lot à la revue (ADR-0023) : le motif est obligatoire — un rejet sans raison n'aide ni
 * l'auteur du scénario, ni la traçabilité. L'approbation, elle, ne porte pas de charge utile.
 */
export const rejectSimulationSchema = z.object({
    reason: z.string().trim().min(1).max(500),
});
/**
 * Assistance à la rédaction (ADR-0021, mode accompagné) : depuis une description en langage naturel,
 * l'assistant (ADR-0016) **propose un scénario structuré** que l'utilisateur ajuste. La sortie est
 * validée par `simulationScenarioSchema` côté serveur — un texte non exploitable est rejeté (`422`).
 */
export const simulationAssistRequestSchema = z.object({
    description: z.string().trim().min(1).max(2000),
});
export const simulationAssistReplySchema = z.object({
    scenario: simulationScenarioSchema,
});
/**
 * Fond photoréaliste du Récapitulatif (ADR-0033) : l'organisation associe **manuellement** une image
 * de sa base (`ExtractedImage`) à un environnement. Le corps ne porte que l'`imageId` — l'environnement
 * est dans l'URL, l'appartenance de l'image est revérifiée côté serveur (cloisonnement).
 */
export const setSimBackdropSchema = z.object({
    imageId: z.string().min(1),
});
/** Sélection courante d'un fond (pour l'UI de curation) : quel `imageId` pour quel environnement. */
export const simBackdropSelectionSchema = z.object({
    environment: simulationEnvironmentSchema,
    imageId: z.string(),
});
//# sourceMappingURL=simulation.js.map